import { track } from '@shared/events';
import { redirect } from '@tanstack/react-router';

import config from '@/config';
import { AUTH_KEY, saveTokenLinkSession, clearSessionCache } from '@shared/auth';
import type { RouteGuardContext } from './route-protection';

/**
 * `token` auth's route guard -- a `beforeLoad`-shaped replacement for the
 * old `shared/auth/authentication.hooks.tsx`'s `useAuth()`/`authToken()`,
 * found while doing Section 08's "auth guards moved into beforeLoad" item
 * (2026-08-19). Lives here (`app/routing/`), not `shared/auth/`, because it
 * implements `route-protection.ts`'s `RouteGuard` contract directly --
 * `shared` can't import from `app`, so a guard shaped for this file's own
 * types has to live at or above this layer.
 *
 * That replaced code had two real bugs, not a style problem to tidy up:
 *
 * 1. `authToken()` called `useNavigate()`/`useLocation()` -- real React
 *    hooks -- from a plain helper function invoked inside a `useEffect`
 *    callback, not during render. An invalid hook call by React's own
 *    rules, on whatever code path actually reached it.
 * 2. `authToken()` was only ever called after its caller had already
 *    checked `if (!token) return`, so its own `if (!token) { ... }`
 *    branch -- the one that read `redirect` and actually called
 *    `navigate({ to: '/' })` -- could never execute. `redirect: true` vs
 *    `redirect: false` made zero behavioural difference: nothing using
 *    this hook with `redirect: true` (`app/routing/lazy/issue.lazy.tsx`'s
 *    default issue-route gate, `poets`/`morningstar`'s `AuthIssue`/
 *    `AuthCollection`) was actually being protected.
 *
 * This function fixes both by construction: it never calls a React hook,
 * and `redirectIfUnauthenticated` is now the one and only thing deciding
 * whether an unauthenticated reader with no usable token gets sent home.
 *
 * No-ops entirely unless the tenant is actually on `token` auth -- safe to
 * wire in as a default for every template, the same reasoning
 * `issue.lazy.tsx`'s old default gate already relied on.
 */
export async function tokenAuthGuard(
	context: RouteGuardContext,
	{ redirectIfUnauthenticated }: { redirectIfUnauthenticated: boolean },
): Promise<void> {
	if (!config?.app?.auth?.enabled || config.app.auth.type !== 'token') {
		return;
	}

	const token = (context.search as { token?: string })?.token;
	const isAuthenticated = !!localStorage.getItem(AUTH_KEY);

	if (isAuthenticated) {
		// A stale ?token= left in the URL after a session already exists --
		// strip it rather than re-exchange it. `replace: true` so the
		// cleanup doesn't add a history entry a reader would have to click
		// "back" through.
		if (token) {
			throw redirect({ to: context.pathname, search: {}, replace: true });
		}
		return;
	}

	if (token) {
		let exchanged = false;
		try {
			await authWithToken(token);
			exchanged = true;
		} catch (e) {
			// Same log-and-continue original behaviour -- a failed exchange
			// isn't fatal on its own; `redirectIfUnauthenticated` below still
			// decides what happens next, same as the no-token case.
			if ((e as Error).message !== 'Failed to fetch') {
				console.error(`Token authentication failed: ${(e as Error).message}`);
			}
		}
		if (exchanged) {
			throw redirect({ to: context.pathname, search: {}, replace: true });
		}
	}

	// Not authenticated, and no token that just proved it (either absent,
	// or present but failed to exchange). Only routes that opted into
	// enforcement send the reader home for this -- `home` itself must stay
	// reachable with no session at all, since it's what shows the token/
	// login entry point in the first place.
	if (redirectIfUnauthenticated) {
		// A route the reader may not open without signing in.
		track('paywall_hit', { gate: 'route', reason: 'login_required' });
		throw redirect({ to: '/', replace: true });
	}
}

async function authWithToken(token: string) {
	const url = [
		'https://api.canvasflow.io/v1/apptoken?',
		new URLSearchParams({ token }).toString(),
	].join('');

	const r = await fetch(url);
	const response = await r.json();
	if (response.status === 'success') {
		const { payload } = response;
		saveTokenLinkSession(payload.expirationDate);
		// A new session establishing itself via the URL token, not a
		// recurring check -- this function only runs when `?token=` is
		// actually present (see the caller above). See
		// `clearSessionCache`'s doc comment for why a session boundary
		// wipes offline content either direction.
		void clearSessionCache();
		return;
	}

	if (response.error) {
		throw new Error(response.error);
	}

	throw new Error('Failed authentication');
}
